import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Table, Spinner, Row, Col } from 'react-bootstrap';
import styled from '@emotion/styled';
import { callCenterApi } from '@/_services/api/callCenter';
import { toast } from 'react-toastify';
import {
    BsGraphUp,
    BsCheckCircle,
    BsXCircle,
    BsArrowRepeat,
    BsPhone,
    BsPerson,
    BsCalendar,
    BsListCheck,
} from 'react-icons/bs';
import OurAccordion from '@/_elements/OurAccordion';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import OpenCallPageButton from './OpenCallPageButton';
import dayjs from 'dayjs';
const AUTO_REFRESH_INTERVAL = 10000;

const MetricCard = styled.div`
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    transition: all 0.2s;
    border-color: #004c22;
    box-shadow: 0 2px 8px rgba(0, 76, 34, 0.1);
`;

const MetricIconWrapper = styled.div`
    color: ${(props) => props.color || '#004c22'};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 18px;
        height: 18px;
    }
`;
const MetricHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
`;
const MetricNumber = styled.div`
    font-weight: 600;
    font-size: 1.75rem;
    line-height: 1;
    margin-top: 0.25rem;
`;
const MetricLabel = styled.div`
    font-size: 1.2rem;
    font-weight: 500;
    line-height: 1.2;
`;
const ModalHeaderContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-right: 1rem;
`;
const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;
const StatusBadge = ({ status }) => {
    const statusConfig = {
        pending: { color: '#6c757d', bg: '#f8f9fa', text: 'dark' },
        processing: { color: '#0d6efd', bg: '#e7f1ff', text: 'primary' },
        retry_pending: { color: '#fd7e14', bg: '#fff3e6', text: 'warning' },
        completed: { color: '#198754', bg: '#d4edda', text: 'success' },
        failed: { color: '#dc3545', bg: '#f8d7da', text: 'danger' },
        max_retries: { color: '#212529', bg: '#e9ecef', text: 'dark' },
    };

    const config = statusConfig[status] || { color: '#17a2b8', bg: '#d1ecf1', text: 'info' };

    return (
        <Badge
            bg={config.text}
            style={{
                backgroundColor: config.bg,
                color: config.color,
                padding: '0.35em 0.65em',
                borderRadius: '6px',
            }}
        >
            {(status || '').replace(/_/g, ' ').toUpperCase()}
        </Badge>
    );
};

export const CallStatusBadge = ({ status }) => {
    const map = {
        queued: 'warning',
        ringing: 'info',
        in_progress: 'primary',
        completed: 'success',
        failed: 'danger',
        no_answer: 'secondary',
        busy: 'warning',
    };
    const badgeName = map[status] || 'info';
    return <span className={`badge badge-${badgeName}`}>{status}</span>;
};
const Component = ({ show, onHide, campaignId, campaignName }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchMetrics = async () => {
        try {
            if (loading) {
                return;
            }
            setLoading(true);
            const res = await callCenterApi.getCampaignQueueStatus(campaignId);
            setMetrics(res);
        } catch (error) {
            if (error?.name === 'AbortError') {
                return;
            }
            console.error('Error fetching metrics:', error);
            toast.error(error?.message || 'Failed to load campaign metrics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let interval;
        if (show) {
            fetchMetrics();
            interval = setInterval(() => {
                fetchMetrics();
            }, AUTO_REFRESH_INTERVAL);
        } else {
            setMetrics({});
        }

        return () => {
            clearInterval(interval);
        };
    }, [show, campaignId]);

    const processedContacts = metrics?.processedContacts || [];
    const displayContacts = [...processedContacts].reverse();

    const totalContacts = ((metrics?.campaignSnapshot || {})?.contacts || []).length;
    const successfulContacts = processedContacts.filter((c) => c.currentStatus === 'completed').length;
    const failedContacts = processedContacts.filter((c) => ['failed', 'max_retries'].includes(c.currentStatus)).length;
    const totalCallsMade = processedContacts.reduce((acc, curr) => acc + (curr.attempts?.length || 0), 0);

    const summaryStats = [
        {
            label: 'Total Contacts',
            value: totalContacts,
            icon: <BsPerson />,
            color: '#004c22',
        },
        {
            label: 'Processed Contacts',
            value: processedContacts.length,
            icon: <BsListCheck />,
            color: '#0d6efd',
        },
        {
            label: 'Successful Calls',
            value: successfulContacts,
            icon: <BsCheckCircle />,
            color: '#198754',
        },
        {
            label: 'Failed Calls',
            value: failedContacts,
            icon: <BsXCircle />,
            color: '#dc3545',
        },
        {
            label: 'Calls Made',
            value: totalCallsMade,
            icon: <BsPhone />,
            color: '#6c757d',
        },
    ];

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header className="border-bottom">
                <ModalHeaderContent>
                    <HeaderLeft>
                        <BsGraphUp className="text-primary" />
                        <div>
                            <div className="text-muted small">
                                Campaign Metrics: <span className="ml-4">{campaignName}</span>
                            </div>
                        </div>
                    </HeaderLeft>
                    <Button
                        variant="outline-primary"
                        onClick={fetchMetrics}
                        disabled={loading}
                        className="d-flex align-items-center gap-2"
                        size="sm"
                    >
                        <BsArrowRepeat className={loading ? 'spin' : ''} />
                    </Button>
                </ModalHeaderContent>
            </Modal.Header>

            <Modal.Body>
                {loading && !metrics ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mt-2 text-muted small">Loading campaign metrics...</p>
                    </div>
                ) : (
                    <>
                        {/* Clean metric cards with icon on top */}
                        <Row className="g-3 mb-4">
                            {summaryStats.map((stat, idx) => (
                                <Col key={idx} xs={6} sm={6} md={4} lg>
                                    <MetricCard>
                                        {/* Row 1: Left = Label, Right = Icon */}
                                        <MetricHeader>
                                            <MetricLabel>{stat.label}</MetricLabel>
                                            <MetricIconWrapper color={stat.color}>{stat.icon}</MetricIconWrapper>
                                        </MetricHeader>

                                        {/* Row 2: Count */}
                                        <MetricNumber>{stat.value}</MetricNumber>
                                    </MetricCard>
                                </Col>
                            ))}
                        </Row>
                        {/* Processed Contacts */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h3 className="mb-0 d-flex align-items-center gap-2">
                                <BsListCheck className="mr-2" />
                                Processed Contacts
                                <Badge bg="light" text="dark" className="ms-1">
                                    {displayContacts.length}
                                </Badge>
                            </h3>
                        </div>

                        {displayContacts.length === 0 ? (
                            <div className="text-center py-3 border rounded bg-light">
                                <p className="text-muted mb-0 small">No contacts processed yet</p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {displayContacts.map((contact, idx) => (
                                    <div
                                        key={contact.contact || idx}
                                        className="border border-primary rounded mt-3 "
                                        style={{
                                            boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
                                        }}
                                    >
                                        <OurAccordion
                                            titleTag={null}
                                            defaultShow={false}
                                            title={
                                                <div className="d-flex justify-content-between align-items-center w-100 p-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <BsPerson className="text-primary" />
                                                        <div className="d-flex justify-content-between align-items-center w-100 p-2">
                                                            <div style={{ fontWeight: '700' }}>
                                                                {contact.name || 'Unknown Contact'}
                                                            </div>
                                                            <div className="text-muted ml-3">
                                                                {contact.phone || 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <StatusBadge status={contact.currentStatus} />
                                                </div>
                                            }
                                            contentStyle={{ padding: 0 }}
                                        >
                                            <div className="p-2 bg-light">
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {/* <div className="d-flex align-items-center gap-1">
                                                            <BsClock className="text-muted " />
                                                            <span className="text-muted ml-2">Retries:</span>
                                                            <span>{contact.retryCount}</span>
                                                        </div>
                                                        <div>|</div> */}
                                                        <div className="d-flex align-items-center gap-1">
                                                            <BsListCheck className="text-muted" />
                                                            <span className="text-muted ml-2">Attempts:</span>
                                                            <span>{contact.attempts?.length || 0}</span>
                                                        </div>
                                                    </div>
                                                    {contact.nextAttemptAt && (
                                                        <div className="d-flex align-items-center gap-1">
                                                            <BsCalendar className="text-muted" />
                                                            <span className="text-muted ml-2">
                                                                Next Call Attempt At:{' '}
                                                                {new Date(contact.nextAttemptAt).toLocaleString()}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                <Table responsive size="sm" className="mb-0 border bg-white">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="text-muted small">Time</th>
                                                            <th className="text-muted small">Status</th>
                                                            <th className="text-muted small">Result</th>
                                                            {/* <th className="text-muted small">Error</th> */}
                                                            <th className="text-muted small">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(contact.attempts || []).map((attempt, aIdx) => (
                                                            <tr key={aIdx}>
                                                                <td className="small" style={{ whiteSpace: 'nowrap' }}>
                                                                    {new Date(attempt.timestamp).toLocaleString()}
                                                                </td>
                                                                <td>
                                                                    <CallStatusBadge
                                                                        status={
                                                                            (attempt?.call || {})?.callStatus ||
                                                                            attempt?.status
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="small" style={{}}>
                                                                    {attempt.result || '-'}
                                                                </td>
                                                                {/* <td className="small text-danger">
                                                                    {attempt.errorMessage || '-'}
                                                                </td> */}
                                                                <td>
                                                                    <OpenCallPageButton
                                                                        callId={(attempt?.call || {})?._id}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {(!contact.attempts || contact.attempts.length === 0) && (
                                                            <tr>
                                                                <td colSpan="4" className="text-center py-2 text-muted">
                                                                    No attempt history available
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </OurAccordion>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="border-top py-2 d-flex justify-content-between align-items-center">
                <div className="">
                    <div>
                        <span className="text-muted text-sm" style={{ fontSize: '14px' }}>
                            Started: {metrics?.startedAt && dayjs(metrics?.startedAt).format('MMMM D, YYYY h:mm A')} -{' '}
                            {metrics?.endedAt && dayjs(metrics?.endedAt).format('MMMM D, YYYY h:mm A')}
                        </span>
                    </div>
                </div>
                <Button variant="secondary" onClick={onHide} size="sm" className="flex-shrink-0">
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const CampaignMetricsModal = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default CampaignMetricsModal;
